import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  FileImageOutlined,
  FileOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShopOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import useAppConfig from "hooks/access-control/useAppConfig";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import olreadiaService from "services/olreadiaService";
import { formatDate, organizationName, readable, statusColor } from "workspaces/workspaceUtils";
import { OLIVEEB_COLORS } from "workspaces/colors";
import "./OrganizationsPage.css";

const { Paragraph, Text, Title } = Typography;
const { TextArea } = Input;

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);
const trim = (value) => String(value || "").trim();
const STATUS_OPTIONS = ["PENDING_REVIEW", "DRAFT", "ACTIVE", "INACTIVE", "SUSPENDED"];
const LOCATION_STATUS_OPTIONS = ["ACTIVE", "INVITED", "INACTIVE"];

const addressText = (source) => {
  const location = source?.locationInformation || source || {};
  return [
    location.addressLine1,
    location.addressLine2,
    location.city,
    location.county,
    location.state,
    location.zipCode,
    location.country,
  ].filter(Boolean).join(", ");
};

const contactText = (entity) => {
  const contact = entity?.contactInformation || {};
  return [
    contact.primaryEmail || entity?.email,
    contact.primaryPhone || entity?.phone,
    contact.websiteUrl || entity?.websiteUrl,
  ].filter(Boolean).join(" • ");
};

const serviceLabel = (service) => service?.name || service?.label || readable(service?.code, "Service");
const locationTypeLabel = (value) => value?.label || value?.name || readable(value?.code || value, "Location type");
const orgInitial = (entity) => organizationName(entity, "O").charAt(0).toUpperCase();
const mapUrl = (location) => {
  const query = addressText(location) || location?.name || "dealership location";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

const extractOrganizationLocation = (components = [], placeId = "", geometry = {}) => {
  const safeComponents = Array.isArray(components) ? components : [];
  const get = (type, useShort = false) => {
    const component = safeComponents.find((item) => Array.isArray(item.types) && item.types.includes(type));
    return useShort ? component?.short_name || "" : component?.long_name || "";
  };
  const streetNumber = get("street_number");
  const route = get("route");

  return {
    addressLine1: [streetNumber, route].filter(Boolean).join(" "),
    addressLine2: "",
    city: get("locality") || get("sublocality") || get("administrative_area_level_2"),
    county: get("administrative_area_level_2"),
    state: get("administrative_area_level_1", true),
    country: get("country", true) || get("country") || "USA",
    zipCode: get("postal_code"),
    latitude: typeof geometry?.location?.lat === "number" ? geometry.location.lat : null,
    longitude: typeof geometry?.location?.lng === "number" ? geometry.location.lng : null,
    placeId: placeId || null,
  };
};

const fileIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return /^(https?:|data:|blob:)/i.test(value) ? "" : value;
  return value.fileId || value.id || value.uuid || value.file?.fileId || value.file?.id || value.file?.uuid || "";
};

const fileUrlValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return /^(https?:|data:|blob:)/i.test(value) ? value : "";
  return (
    value.signedUrl ||
    value.signedURL ||
    value.signed_url ||
    value.downloadUrl ||
    value.downloadURL ||
    value.download_url ||
    value.previewUrl ||
    value.previewURL ||
    value.preview_url ||
    value.publicUrl ||
    value.publicURL ||
    value.public_url ||
    value.fileUrl ||
    value.fileURL ||
    value.file_url ||
    value.mediaUrl ||
    value.mediaURL ||
    value.media_url ||
    value.url ||
    value.uri ||
    value.href ||
    value.file?.signedUrl ||
    value.file?.signedURL ||
    value.file?.signed_url ||
    value.file?.downloadUrl ||
    value.file?.downloadURL ||
    value.file?.download_url ||
    value.file?.previewUrl ||
    value.file?.publicUrl ||
    value.file?.fileUrl ||
    value.file?.url ||
    value.file?.uri ||
    ""
  );
};

const firstFileUrl = (...values) => values.map(fileUrlValue).find(Boolean) || "";

const csvToList = (value) =>
  String(value || "").split(/[\n,]/).map((item) => item.trim()).filter(Boolean);

const listToCsv = (value) =>
  Array.isArray(value) ? value.map(fileIdValue).filter(Boolean).join(", ") : fileIdValue(value);

const keepExistingUrlOnlyFile = (value) => Boolean(fileUrlValue(value) && !fileIdValue(value));

const compactPatchObject = (value) =>
  Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined));

const organizationFileItems = (entity = {}) => {
  const baseItems = [
    {
      key: "certificate",
      label: "Certificate",
      value: entity.certificate || entity.certificateFile || entity.certificateDocument,
      url: firstFileUrl(entity.certificate, entity.certificateUrl, entity.certificateUri, entity.certificateSignedUrl, entity.certificateDownloadUrl),
      kind: "document",
    },
    {
      key: "logo",
      label: "Logo",
      value: entity.logo || entity.logoFile,
      url: firstFileUrl(entity.logo, entity.logoUrl, entity.logoUri, entity.logoSignedUrl, entity.logoDownloadUrl),
      kind: "image",
    },
    {
      key: "profilePhoto",
      label: "Profile photo",
      value: entity.profilePhoto || entity.profilePhotoFile,
      url: firstFileUrl(entity.profilePhoto, entity.profilePhotoUrl, entity.profilePhotoUri, entity.profilePhotoSignedUrl, entity.profilePhotoDownloadUrl),
      kind: "image",
    },
  ];
  const otherDocs = [entity.otherDocs, entity.otherDocuments, entity.documents]
    .find(Array.isArray) || [];
  return [
    ...baseItems,
    ...otherDocs.map((doc, index) => ({ key: `other-${index}`, label: `Document ${index + 1}`, value: doc, kind: "document" })),
  ].map((item) => ({ ...item, fileId: fileIdValue(item.value), url: item.url || fileUrlValue(item.value) }))
    .filter((item) => item.fileId || item.url);
};

const organizationInitialValues = (entity = {}) => {
  const contact = entity.contactInformation || {};
  const location = entity.locationInformation || {};
  const services = entity.organizationOfferings || entity.offerings || entity.services || [];
  return {
    name: organizationName(entity, ""),
    description: entity.description || "",
    ein: entity.ein || "",
    certificate: fileIdValue(entity.certificate),
    logo: fileIdValue(entity.logo),
    profilePhoto: fileIdValue(entity.profilePhoto),
    otherDocs: listToCsv(entity.otherDocs),
    status: entity.status || "PENDING_REVIEW",
    organizationLocationTypeId: entity.organizationLocationTypeId || entity.businessLocationType?.id || entity.organizationLocationType?.id,
    primaryEmail: contact.primaryEmail || entity.email || "",
    secondaryEmail: contact.secondaryEmail || "",
    primaryPhone: contact.primaryPhone || entity.phone || "",
    secondaryPhone: contact.secondaryPhone || "",
    websiteUrl: contact.websiteUrl || entity.websiteUrl || "",
    contactPersonName: contact.contactPersonName || "",
    contactPersonTitle: contact.contactPersonTitle || "",
    addressLine1: location.addressLine1 || "",
    addressLine2: location.addressLine2 || "",
    city: location.city || "",
    county: location.county || "",
    state: location.state || "",
    zipCode: location.zipCode || "",
    country: location.country || "USA",
    serviceIds: services.map((service) => service.id).filter(Boolean),
  };
};

const organizationPayload = (values, existing = {}) => {
  const otherDocs = csvToList(values.otherDocs);
  const originalOtherDocs = Array.isArray(existing.otherDocs) ? existing.otherDocs : [];
  const fileValue = (field) => {
    const nextValue = trim(values[field]);
    if (nextValue) return nextValue;
    return keepExistingUrlOnlyFile(existing[field]) ? undefined : null;
  };

  return compactPatchObject({
    name: trim(values.name),
    description: trim(values.description),
    ein: trim(values.ein) || null,
    certificate: fileValue("certificate"),
    logo: fileValue("logo"),
    profilePhoto: fileValue("profilePhoto"),
    otherDocs:
      otherDocs.length > 0 || !originalOtherDocs.some(keepExistingUrlOnlyFile)
        ? otherDocs
        : undefined,
    status: values.status || "PENDING_REVIEW",
    organizationLocationTypeId: values.organizationLocationTypeId,
    contactInformation: {
    primaryEmail: trim(values.primaryEmail) || null,
    secondaryEmail: trim(values.secondaryEmail) || null,
    primaryPhone: trim(values.primaryPhone) || null,
    secondaryPhone: trim(values.secondaryPhone) || null,
    websiteUrl: trim(values.websiteUrl) || null,
    contactPersonName: trim(values.contactPersonName) || null,
    contactPersonTitle: trim(values.contactPersonTitle) || null,
    },
    locationInformation: {
    addressLine1: trim(values.addressLine1) || null,
    addressLine2: trim(values.addressLine2) || null,
    city: trim(values.city) || null,
    county: trim(values.county) || null,
    state: trim(values.state) || null,
    zipCode: trim(values.zipCode) || null,
    country: trim(values.country) || "USA",
    },
  });
};

const patchOrganizationProps = async (organizationId, payload, actorId) => {
  let nextEntity = null;
  for (const [name, value] of Object.entries(payload)) {
    if (value !== undefined) {
      const updated = await olreadiaService.organizations.updateProp(organizationId, { name, value }, actorId);
      if (updated) nextEntity = updated;
    }
  }
  return nextEntity;
};

const locationInitialValues = (location = {}, organization = {}) => {
  const contact = location.contactInformation || {};
  const info = location.locationInformation || {};
  const services = location.organizationOfferings || location.offerings || location.services || [];
  return {
    name: location.name || "",
    status: location.status || "ACTIVE",
    organizationLocationTypeId: location.organizationLocationTypeId || location.businessLocationType?.id || location.organizationLocationType?.id,
    description: location.description || "",
    primaryEmail: contact.primaryEmail || organization.contactInformation?.primaryEmail || "",
    secondaryEmail: contact.secondaryEmail || "",
    primaryPhone: contact.primaryPhone || organization.contactInformation?.primaryPhone || "",
    secondaryPhone: contact.secondaryPhone || "",
    websiteUrl: contact.websiteUrl || organization.contactInformation?.websiteUrl || "",
    contactPersonName: contact.contactPersonName || organization.contactInformation?.contactPersonName || "",
    contactPersonTitle: contact.contactPersonTitle || organization.contactInformation?.contactPersonTitle || "",
    addressLine1: info.addressLine1 || "",
    addressLine2: info.addressLine2 || "",
    city: info.city || "",
    county: info.county || "",
    state: info.state || "",
    zipCode: info.zipCode || "",
    country: info.country || "USA",
    serviceIds: services.map((service) => service.id).filter(Boolean),
  };
};

const locationPayload = (values) => ({
  name: trim(values.name),
  status: values.status || "ACTIVE",
  description: trim(values.description),
  organizationLocationTypeId: values.organizationLocationTypeId,
  contactInformation: {
    primaryEmail: trim(values.primaryEmail) || null,
    secondaryEmail: trim(values.secondaryEmail) || null,
    primaryPhone: trim(values.primaryPhone) || null,
    secondaryPhone: trim(values.secondaryPhone) || null,
    websiteUrl: trim(values.websiteUrl) || null,
    contactPersonName: trim(values.contactPersonName) || null,
    contactPersonTitle: trim(values.contactPersonTitle) || null,
  },
  locationInformation: {
    addressLine1: trim(values.addressLine1) || null,
    addressLine2: trim(values.addressLine2) || null,
    city: trim(values.city) || null,
    county: trim(values.county) || null,
    state: trim(values.state) || null,
    zipCode: trim(values.zipCode) || null,
    country: trim(values.country) || "USA",
  },
});

function DetailRow({ label, value, onEdit, editing }) {
  return (
    <div className="organizations-detail-row">
      <span>
        <Text className="organizations-detail-label">{label}</Text>
        <Text>{value || "-"}</Text>
      </span>
      {onEdit ? (
        <Button
          className="organizations-row-edit"
          size="small"
          type="text"
          icon={<EditOutlined />}
          loading={editing}
          onClick={onEdit}
        />
      ) : null}
    </div>
  );
}

function FileThumb({ item, url, loading, onPreview }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(url) && !imageFailed;
  return (
    <button type="button" className="organizations-file-thumb" onClick={onPreview}>
      <span className="organizations-file-thumb__media">
        {showImage ? <img src={url} alt={item.label} onError={() => setImageFailed(true)} /> : item.kind === "image" ? <FileImageOutlined /> : <FileOutlined />}
      </span>
      <span className="organizations-file-thumb__copy">
        <strong>{item.label}</strong>
        <span>{loading ? "Loading preview..." : "Preview"}</span>
      </span>
    </button>
  );
}

function AddressAutocomplete({ googleMapsApiKey }) {
  const form = Form.useFormInstance();
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const fetchPredictions = async (value) => {
    setQuery(value);
    if (!googleMapsApiKey || value.trim().length < 3) {
      setPredictions([]);
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(`https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(value)}&types=address&key=${googleMapsApiKey}`);
      const json = await response.json();
      setPredictions(
        Array.isArray(json.predictions)
          ? json.predictions.map((item) => ({ placeId: item.place_id, description: item.description }))
          : [],
      );
    } catch {
      setPredictions([]);
      message.error("Failed to search addresses.");
    } finally {
      setLoading(false);
    }
  };

  const selectPrediction = async (prediction) => {
    if (!googleMapsApiKey || !prediction?.placeId) return;
    try {
      setSelecting(true);
      const response = await fetch(`https://maps.googleapis.com/maps/api/place/details/json?place_id=${encodeURIComponent(prediction.placeId)}&fields=address_component,geometry,place_id&key=${googleMapsApiKey}`);
      const json = await response.json();
      const result = json.result || {};
      const next = extractOrganizationLocation(result.address_components, result.place_id || prediction.placeId, result.geometry);
      form.setFieldsValue(next);
      setQuery(prediction.description || "");
      setPredictions([]);
    } catch {
      message.error("Failed to fetch address details.");
    } finally {
      setSelecting(false);
    }
  };

  return (
    <div className="organizations-address-search">
      <Input.Search
        allowClear
        value={query}
        loading={loading || selecting}
        placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Address search unavailable"}
        disabled={!googleMapsApiKey}
        onChange={(event) => fetchPredictions(event.target.value)}
        onSearch={fetchPredictions}
      />
      {predictions.length ? (
        <div className="organizations-address-suggestions">
          {predictions.map((prediction) => (
            <button key={prediction.placeId} type="button" onClick={() => selectPrediction(prediction)}>
              <EnvironmentOutlined />
              <span>{prediction.description}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ManagedFormFields({ locationTypeOptions, serviceOptions, location = false, googleMapsApiKey = "" }) {
  return (
    <>
      <div className="organizations-form-section">
        <Text className="organizations-section-title">{location ? "Location details" : "Dealership details"}</Text>
        <Form.Item name="name" label={location ? "Location name" : "Dealership name"} rules={[{ required: true, message: "Enter name." }]}>
          <Input />
        </Form.Item>
        <Form.Item name="organizationLocationTypeId" label={location ? "Location type" : "Dealership type"} rules={[{ required: true, message: "Select type." }]}>
          <Select showSearch optionFilterProp="label" options={locationTypeOptions} />
        </Form.Item>
        <Space.Compact block>
          <Form.Item name="status" label="Status" className="organizations-compact-item">
            <Select options={(location ? LOCATION_STATUS_OPTIONS : STATUS_OPTIONS).map((value) => ({ value, label: readable(value) }))} />
          </Form.Item>
          {!location ? (
            <Form.Item name="ein" label="EIN" className="organizations-compact-item"><Input /></Form.Item>
          ) : null}
        </Space.Compact>
        <Form.Item name="description" label="Description"><TextArea rows={2} /></Form.Item>
      </div>
      <div className="organizations-form-section">
        <Text className="organizations-section-title">{location ? "Location service offerings" : "Dealership offerings"}</Text>
        <Form.Item name="serviceIds" label="Services">
          <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={serviceOptions} />
        </Form.Item>
      </div>
      <div className="organizations-form-section">
        <Text className="organizations-section-title">Contact</Text>
        <Space.Compact block>
          <Form.Item name="primaryEmail" label="Primary email" className="organizations-compact-item"><Input /></Form.Item>
          <Form.Item name="secondaryEmail" label="Secondary email" className="organizations-compact-item"><Input /></Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="primaryPhone" label="Primary phone" className="organizations-compact-item"><Input /></Form.Item>
          <Form.Item name="secondaryPhone" label="Secondary phone" className="organizations-compact-item"><Input /></Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="contactPersonName" label="Contact person" className="organizations-compact-item"><Input /></Form.Item>
          <Form.Item name="contactPersonTitle" label="Contact title" className="organizations-compact-item"><Input /></Form.Item>
        </Space.Compact>
        <Form.Item name="websiteUrl" label="Website"><Input /></Form.Item>
      </div>
      <div className="organizations-form-section">
        <Text className="organizations-section-title">Address</Text>
        {location ? <AddressAutocomplete googleMapsApiKey={googleMapsApiKey} /> : null}
        <Form.Item name="addressLine1" label="Address line 1"><Input /></Form.Item>
        <Form.Item name="addressLine2" label="Address line 2"><Input /></Form.Item>
        <Space.Compact block>
          <Form.Item name="city" label="City" className="organizations-compact-item"><Input /></Form.Item>
          <Form.Item name="county" label="County" className="organizations-compact-item"><Input /></Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="state" label="State" className="organizations-compact-item"><Input /></Form.Item>
          <Form.Item name="zipCode" label="Zip code" className="organizations-compact-item"><Input /></Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="country" label="Country" className="organizations-compact-item"><Input /></Form.Item>
        </Space.Compact>
      </div>
      {!location ? (
        <div className="organizations-form-section">
          <Text className="organizations-section-title">Files</Text>
          <Space.Compact block>
            <Form.Item name="certificate" label="Certificate file ID" className="organizations-compact-item"><Input /></Form.Item>
            <Form.Item name="logo" label="Logo file ID" className="organizations-compact-item"><Input /></Form.Item>
          </Space.Compact>
          <Form.Item name="profilePhoto" label="Profile photo file ID"><Input /></Form.Item>
          <Form.Item name="otherDocs" label="Other document file IDs"><TextArea rows={2} /></Form.Item>
        </div>
      ) : null}
    </>
  );
}

export default function OrganizationDetailsPage() {
  const { organizationId } = useParams();
  const [searchParams] = useSearchParams();
  const queryWorkspaceId = searchParams.get("wid") || "";
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appConfig } = useAppConfig();
  const { uploadFile, uploading: fileUploading } = useFileUpload();
  const actorId = user?.id || user?.userId || "";
  const [organizationForm] = Form.useForm();
  const [locationForm] = Form.useForm();
  const [organization, setOrganization] = useState(null);
  const [locationTypes, setLocationTypes] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organizationEditorOpen, setOrganizationEditorOpen] = useState(false);
  const [locationEditor, setLocationEditor] = useState(null);
  const [locationDetails, setLocationDetails] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [propEditor, setPropEditor] = useState(null);
  const [updatingPropKey, setUpdatingPropKey] = useState("");
  const [offeringModalOpen, setOfferingModalOpen] = useState(false);
  const [offeringQuery, setOfferingQuery] = useState("");
  const [offeringActionId, setOfferingActionId] = useState("");
  const [offeringSelectionIds, setOfferingSelectionIds] = useState([]);

  const locations = organization?.businessLocations || organization?.organizationLocations || [];
  const services = organization?.organizationOfferings || organization?.offerings || organization?.services || [];
  const files = useMemo(() => organizationFileItems(organization || {}), [organization]);
  const otherDocs = Array.isArray(organization?.otherDocs) ? organization.otherDocs : [];
  const locationTypeOptions = useMemo(() => locationTypes.map((type) => ({ value: type.id, label: locationTypeLabel(type) })), [locationTypes]);
  const serviceOptions = useMemo(() => offerings.filter((service) => service?.id).map((service) => ({ value: service.id, label: serviceLabel(service) })), [offerings]);
  const locationServiceOptions = useMemo(
    () => services.filter((service) => service?.id).map((service) => ({ value: service.id, label: serviceLabel(service) })),
    [services],
  );
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";
  const selectedOfferingIds = useMemo(
    () => new Set(services.map((service) => service.id).filter(Boolean)),
    [services],
  );
  const availableOfferings = useMemo(() => {
    const query = offeringQuery.trim().toLowerCase();
    return offerings
      .filter((service) => service?.active !== false)
      .filter((service) => !service?.id || !selectedOfferingIds.has(service.id))
      .filter((service) => {
        if (!query) return true;
        return [service.name, service.code, service.category, service.description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      });
  }, [offeringQuery, offerings, selectedOfferingIds]);

  const loadData = useCallback(async () => {
    if (!organizationId || !actorId) return;
    setLoading(true);
    try {
      const [entity, typePage, offeringPage] = await Promise.all([
        olreadiaService.organizations.get(organizationId, queryWorkspaceId ? { wid: queryWorkspaceId } : {}),
        olreadiaService.businessLocationTypes.list({ active: true, size: 5000 }),
        olreadiaService.serviceOfferings.list({ active: true, size: 5000 }),
      ]);
      setOrganization(entity || null);
      setLocationTypes(pageContent(typePage));
      setOfferings(pageContent(offeringPage));
    } catch (error) {
      message.error(error?.message || "Failed to load dealership.");
    } finally {
      setLoading(false);
    }
  }, [actorId, organizationId, queryWorkspaceId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const syncOrganizationOfferings = async (nextServiceIds = []) => {
    const current = await olreadiaService.serviceOfferings.organizationServices.list(organizationId).catch(() => []);
    const currentIds = new Set(pageContent(current).map((service) => service.id).filter(Boolean));
    const selectedIds = new Set((nextServiceIds || []).filter(Boolean));
    await Promise.all([
      ...[...selectedIds].filter((id) => !currentIds.has(id)).map((id) => olreadiaService.serviceOfferings.organizationServices.add(organizationId, id, actorId)),
      ...[...currentIds].filter((id) => !selectedIds.has(id)).map((id) => olreadiaService.serviceOfferings.organizationServices.remove(organizationId, id, actorId)),
    ]);
  };

  const openOfferingModal = () => {
    setOfferingQuery("");
    setOfferingSelectionIds([]);
    setOfferingModalOpen(true);
  };

  const closeOfferingModal = () => {
    if (!offeringActionId) setOfferingModalOpen(false);
  };

  const toggleOfferingSelection = (service) => {
    if (!service?.id || offeringActionId) return;
    setOfferingSelectionIds((current) =>
      current.includes(service.id)
        ? current.filter((id) => id !== service.id)
        : [...current, service.id],
    );
  };

  const saveSelectedOfferings = async () => {
    if (!organizationId || !offeringSelectionIds.length || offeringActionId) return;
    try {
      setOfferingActionId("saving-offerings");
      await Promise.all(
        offeringSelectionIds.map((serviceId) =>
          olreadiaService.serviceOfferings.organizationServices.add(organizationId, serviceId, actorId),
        ),
      );
      message.success("dealership offerings added.");
      setOfferingSelectionIds([]);
      setOfferingModalOpen(false);
      await loadData();
    } catch (error) {
      message.error(error?.message || "Failed to add dealership offerings.");
    } finally {
      setOfferingActionId("");
    }
  };

  const removeOffering = async (service) => {
    if (!organizationId || !service?.id || offeringActionId) return;
    try {
      setOfferingActionId(service.id);
      await olreadiaService.serviceOfferings.organizationServices.remove(organizationId, service.id, actorId);
      message.success("dealership offering removed.");
      await loadData();
    } catch (error) {
      message.error(error?.message || "Failed to remove dealership offering.");
    } finally {
      setOfferingActionId("");
    }
  };

  const syncLocationOfferings = async (locationId, nextServiceIds = []) => {
    const parentCurrent = await olreadiaService.serviceOfferings.organizationServices.list(organizationId).catch(() => []);
    const parentIds = new Set(pageContent(parentCurrent).map((service) => service.id).filter(Boolean));
    const selectedIds = new Set((nextServiceIds || []).filter(Boolean));
    const missingParentIds = [...selectedIds].filter((id) => !parentIds.has(id));
    if (missingParentIds.length) {
      await Promise.all(
        missingParentIds.map((id) =>
          olreadiaService.serviceOfferings.organizationServices.add(organizationId, id, actorId),
        ),
      );
    }
    const current = await olreadiaService.serviceOfferings.businessLocationServices.list(organizationId, locationId).catch(() => []);
    const currentIds = new Set(pageContent(current).map((service) => service.id).filter(Boolean));
    await Promise.all([
      ...[...selectedIds].filter((id) => !currentIds.has(id)).map((id) => olreadiaService.serviceOfferings.businessLocationServices.add(organizationId, locationId, id, actorId)),
      ...[...currentIds].filter((id) => !selectedIds.has(id)).map((id) => olreadiaService.serviceOfferings.businessLocationServices.remove(organizationId, locationId, id, actorId)),
    ]);
  };

  const openOrganizationEditor = () => {
    organizationForm.setFieldsValue(organizationInitialValues(organization || {}));
    setOrganizationEditorOpen(true);
  };

  const patchEntityProp = async (name, value) => {
    if (!organizationId) return null;
    const updated = await olreadiaService.organizations.updateProp(organizationId, { name, value }, actorId);
    if (updated?.id) setOrganization(updated);
    await loadData();
    return updated;
  };

  const openPropEditor = ({ title, propName, value, multiline = false, options, buildValue }) => {
    setPropEditor({
      title,
      propName,
      value: value == null ? "" : String(value),
      multiline,
      options,
      buildValue,
    });
  };

  const savePropEditor = async () => {
    if (!propEditor?.propName) return;
    try {
      setSaving(true);
      setUpdatingPropKey(propEditor.propName);
      const rawValue = propEditor.value || "";
      const value = propEditor.buildValue ? propEditor.buildValue(rawValue) : trim(rawValue) || null;
      await patchEntityProp(propEditor.propName, value);
      setPropEditor(null);
      message.success("dealership property updated.");
    } catch (error) {
      message.error(error?.message || "Failed to update dealership property.");
    } finally {
      setSaving(false);
      setUpdatingPropKey("");
    }
  };

  const updateContactProperty = (key, label, value) =>
    openPropEditor({
      title: label,
      propName: "contactInformation",
      value,
      buildValue: (next) => ({
        ...(organization?.contactInformation || {}),
        [key]: trim(next) || null,
      }),
    });

  const updateLocationProperty = (key, label, value) =>
    openPropEditor({
      title: label,
      propName: "locationInformation",
      value,
      buildValue: (next) => ({
        ...(organization?.locationInformation || {}),
        [key]: trim(next) || null,
      }),
    });

  const uploadOrganizationFile = async (field, file) => {
    try {
      setUpdatingPropKey(field);
      const result = await uploadFile(file, actorId, {
        purpose: field === "logo" || field === "profilePhoto" ? "PROFILE_PHOTO" : "OTHER",
        preserveOriginal: field === "certificate" || field === "otherDocs",
        confirmUpload: false,
      });
      if (!result?.fileId) throw new Error("Upload failed: missing file ID.");
      if (field === "otherDocs") {
        const existingIds = (organization?.otherDocs || []).map(fileIdValue).filter(Boolean);
        await patchEntityProp("otherDocs", [...existingIds, result.fileId]);
      } else {
        await patchEntityProp(field, result.fileId);
      }
      message.success("dealership file updated.");
    } catch (error) {
      message.error(error?.message || "Failed to update dealership file.");
    } finally {
      setUpdatingPropKey("");
    }
  };

  const clearFileProp = async (field) => {
    try {
      setUpdatingPropKey(field);
      await patchEntityProp(field, field === "otherDocs" ? [] : null);
      message.success("dealership file removed.");
    } catch (error) {
      message.error(error?.message || "Failed to remove dealership file.");
    } finally {
      setUpdatingPropKey("");
    }
  };

  const removeOtherDoc = async (indexToRemove) => {
    try {
      setUpdatingPropKey("otherDocs");
      const nextIds = (organization?.otherDocs || [])
        .filter((_, index) => index !== indexToRemove)
        .map(fileIdValue)
        .filter(Boolean);
      await patchEntityProp("otherDocs", nextIds);
      message.success("Document removed.");
    } catch (error) {
      message.error(error?.message || "Failed to remove document.");
    } finally {
      setUpdatingPropKey("");
    }
  };

  const uploadButtonProps = (field) => ({
    showUploadList: false,
    customRequest: ({ file, onSuccess, onError }) => {
      uploadOrganizationFile(field, file)
        .then(() => onSuccess?.("ok"))
        .catch((error) => onError?.(error));
    },
  });

  const submitOrganization = async (values) => {
    try {
      setSaving(true);
      const payload = organizationPayload(values, organization);
      const entity = await patchOrganizationProps(organizationId, payload, actorId);
      await syncOrganizationOfferings(values.serviceIds);
      setOrganizationEditorOpen(false);
      message.success("dealership updated.");
      setOrganization(entity || organization);
      await loadData();
    } catch (error) {
      message.error(error?.message || "Failed to update dealership.");
    } finally {
      setSaving(false);
    }
  };

  const openLocationCreate = () => {
    locationForm.setFieldsValue(locationInitialValues({}, organization || {}));
    setLocationEditor({ mode: "create", location: null });
  };

  const openLocationEdit = (location) => {
    locationForm.setFieldsValue(locationInitialValues(location, organization || {}));
    setLocationEditor({ mode: "edit", location });
  };

  const submitLocation = async (values) => {
    try {
      setSaving(true);
      const payload = locationPayload(values);
      if (locationEditor?.mode === "edit" && locationEditor.location?.id) {
        const updated = await olreadiaService.organizations.locations.update(organizationId, locationEditor.location.id, payload, actorId);
        await syncLocationOfferings(updated?.id || locationEditor.location.id, values.serviceIds);
      } else {
        const created = await olreadiaService.organizations.locations.create(organizationId, payload, actorId);
        await syncLocationOfferings(created?.id, values.serviceIds);
      }
      setLocationEditor(null);
      message.success("dealership location saved.");
      await loadData();
    } catch (error) {
      message.error(error?.message || "Failed to save dealership location.");
    } finally {
      setSaving(false);
    }
  };

  const deleteLocation = (location) => {
    Modal.confirm({
      title: "Delete dealership location?",
      content: `This will remove ${location.name || "this location"}.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        await olreadiaService.organizations.locations.delete(organizationId, location.id, actorId);
        message.success("dealership location deleted.");
        await loadData();
      },
    });
  };

  const locationServices = locationDetails?.services || locationDetails?.organizationOfferings || [];

  const openFilePreview = async (item) => {
    setFilePreview({ ...item, url: item.url || "" });
  };

  return (
    <div className="project-page organizations-page organizations-details-page">
      <div className="organizations-header">
        <div>
          <Text className="organizations-eyebrow">Dealership Details</Text>
          <Title level={3}>{organizationName(organization, "Dealership")}</Title>
          <Paragraph>{contactText(organization) || addressText(organization) || "Manage dealership profile and locations."}</Paragraph>
        </div>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.organizations)}>Back</Button>
          <Button icon={<ReloadOutlined />} onClick={loadData}>Refresh</Button>
        </Space>
      </div>

      {loading ? (
        <Card><Skeleton active paragraph={{ rows: 10 }} /></Card>
      ) : !organization ? (
        <Card><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="dealership not found" /></Card>
      ) : (
        <div className="organizations-detail-layout organizations-detail-layout--full-height">
            <div className="organizations-detail-main">
              <Card className="organizations-detail-page-hero">
                <div className="organizations-detail-hero">
                  <span className="organizations-avatar is-large">{orgInitial(organization)}</span>
                  <div>
                    <Title level={4}>{organizationName(organization)}</Title>
                    <Space wrap size={[6, 6]}>
                      <Tag color={statusColor(organization.status)}>{readable(organization.status || "Pending Review")}</Tag>
                      <Tag color="blue">{locationTypeLabel(organization.businessLocationType || organization.organizationLocationType)}</Tag>
                      <Tag color="green">{locations.length} locations</Tag>
                      <Tag>{services.length} services</Tag>
                    </Space>
                  </div>
                </div>
              </Card>

              <Card size="small" title={<span className="organizations-section-title"><BankOutlined /> Dealership details</span>}>
                <div className="organizations-detail-grid">
                  <DetailRow
                    label="Name"
                    value={organizationName(organization)}
                    editing={updatingPropKey === "name"}
                    onEdit={() => openPropEditor({ title: "Name", propName: "name", value: organizationName(organization, "") })}
                  />
                  <DetailRow
                    label="Description"
                    value={organization.description}
                    editing={updatingPropKey === "description"}
                    onEdit={() => openPropEditor({ title: "Description", propName: "description", value: organization.description || "", multiline: true })}
                  />
                  <DetailRow
                    label="Type"
                    value={locationTypeLabel(organization.businessLocationType || organization.organizationLocationType)}
                    editing={updatingPropKey === "organizationLocationTypeId"}
                    onEdit={() =>
                      openPropEditor({
                        title: "Dealership type",
                        propName: "organizationLocationTypeId",
                        value: organization.organizationLocationTypeId || organization.businessLocationType?.id || organization.organizationLocationType?.id || "",
                        options: locationTypeOptions,
                      })
                    }
                  />
                  <DetailRow
                    label="Status"
                    value={readable(organization.status || "PENDING_REVIEW")}
                    editing={updatingPropKey === "status"}
                    onEdit={() =>
                      openPropEditor({
                        title: "Status",
                        propName: "status",
                        value: organization.status || "PENDING_REVIEW",
                        options: STATUS_OPTIONS.map((value) => ({ value, label: readable(value) })),
                      })
                    }
                  />
                  <DetailRow
                    label="EIN"
                    value={organization.ein}
                    editing={updatingPropKey === "ein"}
                    onEdit={() => openPropEditor({ title: "EIN", propName: "ein", value: organization.ein || "" })}
                  />
                  <DetailRow label="Created" value={formatDate(organization.createdAt)} />
                </div>
              </Card>

              <Card size="small" title={<span className="organizations-section-title"><MailOutlined /> Contact information</span>}>
                <div className="organizations-detail-grid">
                  <DetailRow label="Primary email" value={organization.contactInformation?.primaryEmail || organization.email} editing={updatingPropKey === "contactInformation"} onEdit={() => updateContactProperty("primaryEmail", "Primary email", organization.contactInformation?.primaryEmail || organization.email)} />
                  <DetailRow label="Secondary email" value={organization.contactInformation?.secondaryEmail} editing={updatingPropKey === "contactInformation"} onEdit={() => updateContactProperty("secondaryEmail", "Secondary email", organization.contactInformation?.secondaryEmail)} />
                  <DetailRow label="Primary phone" value={organization.contactInformation?.primaryPhone || organization.phone} editing={updatingPropKey === "contactInformation"} onEdit={() => updateContactProperty("primaryPhone", "Primary phone", organization.contactInformation?.primaryPhone || organization.phone)} />
                  <DetailRow label="Secondary phone" value={organization.contactInformation?.secondaryPhone} editing={updatingPropKey === "contactInformation"} onEdit={() => updateContactProperty("secondaryPhone", "Secondary phone", organization.contactInformation?.secondaryPhone)} />
                  <DetailRow label="Website" value={organization.contactInformation?.websiteUrl || organization.websiteUrl} editing={updatingPropKey === "contactInformation"} onEdit={() => updateContactProperty("websiteUrl", "Website", organization.contactInformation?.websiteUrl || organization.websiteUrl)} />
                  <DetailRow label="Contact person" value={organization.contactInformation?.contactPersonName} editing={updatingPropKey === "contactInformation"} onEdit={() => updateContactProperty("contactPersonName", "Contact person", organization.contactInformation?.contactPersonName)} />
                  <DetailRow label="Contact title" value={organization.contactInformation?.contactPersonTitle} editing={updatingPropKey === "contactInformation"} onEdit={() => updateContactProperty("contactPersonTitle", "Contact title", organization.contactInformation?.contactPersonTitle)} />
                </div>
              </Card>

              <Card size="small" title={<span className="organizations-section-title"><EnvironmentOutlined /> Dealership physical location</span>}>
                <div className="organizations-detail-grid">
                  <DetailRow label="Address line 1" value={organization.locationInformation?.addressLine1} editing={updatingPropKey === "locationInformation"} onEdit={() => updateLocationProperty("addressLine1", "Address line 1", organization.locationInformation?.addressLine1)} />
                  <DetailRow label="Address line 2" value={organization.locationInformation?.addressLine2} editing={updatingPropKey === "locationInformation"} onEdit={() => updateLocationProperty("addressLine2", "Address line 2", organization.locationInformation?.addressLine2)} />
                  <DetailRow label="City" value={organization.locationInformation?.city} editing={updatingPropKey === "locationInformation"} onEdit={() => updateLocationProperty("city", "City", organization.locationInformation?.city)} />
                  <DetailRow label="County" value={organization.locationInformation?.county} editing={updatingPropKey === "locationInformation"} onEdit={() => updateLocationProperty("county", "County", organization.locationInformation?.county)} />
                  <DetailRow label="State" value={organization.locationInformation?.state} editing={updatingPropKey === "locationInformation"} onEdit={() => updateLocationProperty("state", "State", organization.locationInformation?.state)} />
                  <DetailRow label="Zip code" value={organization.locationInformation?.zipCode} editing={updatingPropKey === "locationInformation"} onEdit={() => updateLocationProperty("zipCode", "Zip code", organization.locationInformation?.zipCode)} />
                  <DetailRow label="Country" value={organization.locationInformation?.country} editing={updatingPropKey === "locationInformation"} onEdit={() => updateLocationProperty("country", "Country", organization.locationInformation?.country)} />
                </div>
              </Card>

              <Card size="small" title={<span className="organizations-section-title"><BankOutlined /> Dealership locations</span>} extra={<Button size="small" icon={<PlusOutlined />} onClick={openLocationCreate}>Add location</Button>}>
                {locations.length ? (
                  <Space direction="vertical" size={8} className="organizations-inner-list">
                    {locations.map((location) => (
                      <div key={location.id || location.name} className="organizations-line-item organizations-line-item--location">
                        <div>
                          <Text strong>{location.name || "dealership location"}</Text>
                          <Text type="secondary">{locationTypeLabel(location.businessLocationType || location.organizationLocationType)}{location.status ? ` • ${readable(location.status)}` : ""}</Text>
                          <Text type="secondary">{addressText(location) || "Address not set"}</Text>
                          {contactText(location) ? <Text type="secondary">{contactText(location)}</Text> : null}
                          {(location.services || location.organizationOfferings || []).length ? (
                            <Space wrap size={[4, 4]} className="organizations-line-services">
                              {(location.services || location.organizationOfferings || []).map((service) => <Tag key={service.id || service.code || service.name}>{serviceLabel(service)}</Tag>)}
                            </Space>
                          ) : null}
                        </div>
                        <Space>
                          <Button size="small" icon={<EnvironmentOutlined />} onClick={() => setLocationDetails(location)}>View</Button>
                          <Button size="small" icon={<EditOutlined />} onClick={() => openLocationEdit(location)}>Edit</Button>
                          {location.id ? <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteLocation(location)}>Delete</Button> : null}
                        </Space>
                      </div>
                    ))}
                  </Space>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No dealership locations yet" />
                )}
              </Card>

              <Card
                size="small"
                title={<span className="organizations-section-title"><ShopOutlined /> Service offerings</span>}
                extra={<Button size="small" icon={<PlusOutlined />} onClick={openOfferingModal}>Add offering</Button>}
              >
                {services.length ? (
                  <div className="organizations-offering-pill-wrap">
                    {services.map((service) => (
                      <button
                        key={service.id || service.code || service.name}
                        type="button"
                        className="organizations-offering-pill"
                        onClick={() => removeOffering(service)}
                        disabled={!service.id || offeringActionId === service.id}
                        title="Remove offering"
                      >
                        <span>{serviceLabel(service)}</span>
                        {offeringActionId === service.id ? <ReloadOutlined spin /> : <CloseOutlined />}
                      </button>
                    ))}
                  </div>
                ) : (
                  <Text type="secondary">No dealership offerings selected.</Text>
                )}
              </Card>
              <Card size="small" title={<span className="organizations-section-title"><FileImageOutlined /> Files</span>} className="organizations-files-card">
                <Space direction="vertical" size={8} className="organizations-inner-list">
                  {["certificate", "logo", "profilePhoto"].map((field) => {
                    const labels = { certificate: "Certificate", logo: "Logo", profilePhoto: "Profile photo" };
                    const existing = files.find((item) => item.key === field);
                    return (
                      <div key={field} className="organizations-file-manage-row">
                        {existing ? (
                          <FileThumb item={existing} url={existing.url} loading={false} onPreview={() => openFilePreview(existing)} />
                        ) : (
                          <div className="organizations-file-empty">
                            <FileOutlined />
                            <Text>{labels[field]}</Text>
                          </div>
                        )}
                        <Space>
                          <Upload {...uploadButtonProps(field)}>
                            <Button size="small" icon={<UploadOutlined />} loading={updatingPropKey === field || fileUploading}>
                              Upload
                            </Button>
                          </Upload>
                          {(existing?.fileId || existing?.url) ? (
                            <Button size="small" danger icon={<CloseOutlined />} loading={updatingPropKey === field} onClick={() => clearFileProp(field)}>
                              Remove
                            </Button>
                          ) : null}
                        </Space>
                      </div>
                    );
                  })}
                  <div className="organizations-file-manage-row">
                    <div>
                      <Text strong>Other documents</Text>
                      <Text type="secondary">{otherDocs.length ? `${otherDocs.length} document${otherDocs.length === 1 ? "" : "s"}` : "No documents added yet."}</Text>
                    </div>
                    <Space>
                      <Upload {...uploadButtonProps("otherDocs")}>
                        <Button size="small" icon={<UploadOutlined />} loading={updatingPropKey === "otherDocs" || fileUploading}>
                          Add document
                        </Button>
                      </Upload>
                      {otherDocs.length ? (
                        <Button size="small" danger icon={<DeleteOutlined />} loading={updatingPropKey === "otherDocs"} onClick={() => clearFileProp("otherDocs")}>
                          Clear
                        </Button>
                      ) : null}
                    </Space>
                  </div>
                  {otherDocs.length ? (
                    <div className="organizations-file-grid">
                      {otherDocs.map((doc, index) => {
                        const item = { key: `other-${index}`, label: `Document ${index + 1}`, value: doc, kind: "document", fileId: fileIdValue(doc), url: fileUrlValue(doc) };
                        return (
                          <div key={`${item.fileId || item.url || index}-${index}`} className="organizations-document-chip">
                            <FileThumb item={item} url={item.url} loading={false} onPreview={() => openFilePreview(item)} />
                            <Button size="small" danger icon={<CloseOutlined />} loading={updatingPropKey === "otherDocs"} onClick={() => removeOtherDoc(index)} />
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </Space>
              </Card>
            </div>

            <SidePromotionSection className="organizations-detail-promotions" />
        </div>
      )}

      <Drawer open={organizationEditorOpen} onClose={() => setOrganizationEditorOpen(false)} title="Edit Dealership" width={660} className="organizations-drawer" destroyOnClose>
        <Form form={organizationForm} layout="vertical" requiredMark={false} onFinish={submitOrganization}>
          <ManagedFormFields locationTypeOptions={locationTypeOptions} serviceOptions={serviceOptions} />
          <div className="organizations-drawer-actions">
            <Button onClick={() => setOrganizationEditorOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Save Dealership</Button>
          </div>
        </Form>
      </Drawer>

      <Drawer
        open={Boolean(propEditor)}
        onClose={() => setPropEditor(null)}
        title={propEditor?.title || "Update property"}
        width={420}
        className="organizations-drawer organizations-property-drawer"
        destroyOnClose
      >
        {propEditor ? (
          <Space direction="vertical" size={12} className="organizations-drawer-stack">
            <Text type="secondary">Update this dealership property only.</Text>
            {propEditor.options ? (
              <Select
                value={propEditor.value}
                options={propEditor.options}
                showSearch
                optionFilterProp="label"
                onChange={(value) => setPropEditor((current) => ({ ...current, value }))}
              />
            ) : propEditor.multiline ? (
              <TextArea
                rows={5}
                value={propEditor.value}
                onChange={(event) => setPropEditor((current) => ({ ...current, value: event.target.value }))}
              />
            ) : (
              <Input
                value={propEditor.value}
                onChange={(event) => setPropEditor((current) => ({ ...current, value: event.target.value }))}
              />
            )}
            <div className="organizations-drawer-actions">
              <Button danger onClick={() => setPropEditor(null)}>Cancel</Button>
              <Button type="primary" loading={saving} onClick={savePropEditor}>
                Done
              </Button>
            </div>
          </Space>
        ) : null}
      </Drawer>

      <Modal
        open={offeringModalOpen}
        onCancel={closeOfferingModal}
        footer={
          <div className="organizations-offering-modal-actions">
            <Button onClick={closeOfferingModal} disabled={Boolean(offeringActionId)}>
              Cancel
            </Button>
            <Button
              type="primary"
              className="organizations-offering-save-button"
              icon={<PlusOutlined />}
              disabled={!offeringSelectionIds.length}
              loading={offeringActionId === "saving-offerings"}
              onClick={saveSelectedOfferings}
            >
              Save {offeringSelectionIds.length ? `(${offeringSelectionIds.length})` : ""}
            </Button>
          </div>
        }
        title="Add dealership offering"
        width={620}
        className="organizations-offering-modal"
        destroyOnClose
      >
        <Space direction="vertical" size={12} className="organizations-drawer-stack">
          <Input
            value={offeringQuery}
            onChange={(event) => setOfferingQuery(event.target.value)}
            placeholder="Search services e.g. detailing, financing"
            disabled={Boolean(offeringActionId)}
            allowClear
          />
          {availableOfferings.length ? (
            <div className="organizations-offering-options-list">
              {availableOfferings.map((service) => (
                <button
                  key={service.id || service.code || service.name}
                  type="button"
                  className={`organizations-offering-option${offeringSelectionIds.includes(service.id) ? " is-selected" : ""}`}
                  onClick={() => toggleOfferingSelection(service)}
                  disabled={!service.id || Boolean(offeringActionId)}
                >
                  <span className="organizations-offering-option__icon"><ShopOutlined /></span>
                  <span className="organizations-offering-option__copy">
                    <Text strong>{serviceLabel(service)}</Text>
                    <Text type="secondary">{readable(service.category || service.code, "Service offering")}</Text>
                    {service.description ? <Text type="secondary">{service.description}</Text> : null}
                  </span>
                  {offeringSelectionIds.includes(service.id) ? <CloseOutlined /> : <PlusOutlined />}
                </button>
              ))}
            </div>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No more service offerings available." />
          )}
        </Space>
      </Modal>

      <Drawer open={Boolean(locationEditor)} onClose={() => setLocationEditor(null)} title={locationEditor?.mode === "edit" ? "Edit dealership location" : "Add dealership location"} width={600} className="organizations-drawer" destroyOnClose>
        <Form form={locationForm} layout="vertical" requiredMark={false} onFinish={submitLocation}>
          <ManagedFormFields location locationTypeOptions={locationTypeOptions} serviceOptions={locationServiceOptions} googleMapsApiKey={googleMapsApiKey} />
          <div className="organizations-drawer-actions">
            <Button onClick={() => setLocationEditor(null)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>Save Location</Button>
          </div>
        </Form>
      </Drawer>

      <Drawer
        open={Boolean(locationDetails)}
        onClose={() => setLocationDetails(null)}
        title="Dealership location"
        width={560}
        className="organizations-drawer organizations-location-drawer"
        destroyOnClose
        extra={
          locationDetails ? (
            <Space>
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  openLocationEdit(locationDetails);
                  setLocationDetails(null);
                }}
              >
                Edit
              </Button>
              {locationDetails.id ? (
                <Button danger icon={<DeleteOutlined />} onClick={() => deleteLocation(locationDetails)}>
                  Delete
                </Button>
              ) : null}
            </Space>
          ) : null
        }
      >
        {locationDetails ? (
          <Space direction="vertical" size={12} className="organizations-drawer-stack">
            <div className="organizations-location-hero">
              <span className="organizations-location-hero__icon"><EnvironmentOutlined /></span>
              <div className="organizations-location-hero__copy">
                <Title level={4}>{locationDetails.name || "dealership location"}</Title>
                <Text>{organizationName(organization)}</Text>
                <Space wrap size={[5, 5]} className="organizations-location-badges">
                  <Tag color={statusColor(locationDetails.status)}>{readable(locationDetails.status || "Active")}</Tag>
                  <Tag color="blue">{locationTypeLabel(locationDetails.businessLocationType || locationDetails.organizationLocationType || locationDetails.type)}</Tag>
                  <Tag color="green">{locationServices.length} services</Tag>
                </Space>
              </div>
            </div>

            {locationDetails.description ? (
              <Card size="small" className="organizations-location-note">
                <Paragraph>{locationDetails.description}</Paragraph>
              </Card>
            ) : null}

            <Card
              size="small"
              title={<span className="organizations-section-title"><MailOutlined /> Contact information</span>}
            >
              <div className="organizations-detail-grid">
                <DetailRow label="Primary email" value={locationDetails.contactInformation?.primaryEmail} />
                <DetailRow label="Secondary email" value={locationDetails.contactInformation?.secondaryEmail} />
                <DetailRow label="Primary phone" value={locationDetails.contactInformation?.primaryPhone} />
                <DetailRow label="Secondary phone" value={locationDetails.contactInformation?.secondaryPhone} />
                <DetailRow label="Website" value={locationDetails.contactInformation?.websiteUrl} />
                <DetailRow label="Contact person" value={locationDetails.contactInformation?.contactPersonName} />
                <DetailRow label="Contact title" value={locationDetails.contactInformation?.contactPersonTitle} />
              </div>
            </Card>

            <Card
              size="small"
              title={<span className="organizations-section-title"><EnvironmentOutlined /> Location information</span>}
            >
              <div className="organizations-detail-grid">
                <DetailRow label="Address" value={addressText(locationDetails)} />
              </div>
              <Button
                className="organizations-map-button"
                icon={<GlobalOutlined />}
                href={mapUrl(locationDetails)}
                target="_blank"
                rel="noreferrer"
                disabled={!addressText(locationDetails)}
              >
                Open Map
              </Button>
            </Card>

            <Card
              size="small"
              title={<span className="organizations-section-title"><ShopOutlined /> Service offerings</span>}
            >
              {locationServices.length ? (
                <div className="organizations-service-card-list">
                  {locationServices.map((service, index) => (
                    <div key={service.id || service.code || service.name || index} className="organizations-service-card">
                      <span className="organizations-service-card__mark">✓</span>
                      <div>
                        <Text strong>{serviceLabel(service)}</Text>
                        {service.category ? <Text type="secondary">{readable(service.category)}</Text> : null}
                        {service.description ? <Text type="secondary">{service.description}</Text> : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Text type="secondary">No service offerings selected.</Text>
              )}
            </Card>
          </Space>
        ) : null}
      </Drawer>

      <Modal open={Boolean(filePreview)} onCancel={() => setFilePreview(null)} footer={filePreview?.url ? <Button type="primary" href={filePreview.url} target="_blank" rel="noreferrer">Open in new tab</Button> : null} title={filePreview?.label || "File preview"} width={760} className="organizations-file-preview-modal" destroyOnClose>
        {filePreview?.url ? (
          <div className="organizations-file-preview">
            <img src={filePreview.url} alt={filePreview.label || "File preview"} onError={(event) => { event.currentTarget.style.display = "none"; const fallback = event.currentTarget.nextElementSibling; if (fallback) fallback.style.display = "grid"; }} />
            <div className="organizations-file-preview__fallback"><FileOutlined /><Text>This file cannot be shown as an image preview.</Text><Button href={filePreview.url} target="_blank" rel="noreferrer">Open file</Button></div>
          </div>
        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Preview is not available for this file." />}
      </Modal>
    </div>
  );
}
